import { useState, useEffect } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { MapPin, CircleCheck, ShieldCheck, Clock4, UserRound } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { api, getUTM } from "@/lib/api";
import { useLang } from "@/lib/i18n";
import { Seo } from "@/lib/seo";
import { SectionHeading, ComplianceNote } from "@/components/shared/Shared";

export default function BeraterFinden() {
  const { t, lang } = useLang();
  const [params] = useSearchParams();
  const [form, setForm] = useState({
    plz: "",
    thema: "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    callback_window: "",
  });
  const [privacy, setPrivacy] = useState(false);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    const plz = params.get("plz") || "";
    const thema = params.get("thema") || "";
    setForm((f) => ({ ...f, plz, thema }));
  }, [params]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async () => {
    const errs = {};
    if (!/^\d{5}$/.test(form.plz)) errs.plz = true;
    if (!form.thema) errs.thema = true;
    if (!form.first_name.trim()) errs.first_name = true;
    if (!form.last_name.trim()) errs.last_name = true;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = true;
    if (!privacy) errs.privacy = true;
    setErrors(errs);
    if (Object.keys(errs).length > 0) return;
    setSubmitting(true);
    try {
      await api.post("/public/forms/rueckruf", {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone || null,
        message: null,
        callback_window: form.callback_window || null,
        product_interest: form.thema,
        consent: { privacy: true, contact: true, marketing: false },
        utm: getUTM(),
        locale: lang,
        extra: { plz: form.plz, thema: form.thema, quelle: "berater_finden" },
      });
      setSuccess(true);
      window.scrollTo(0, 0);
    } catch (e) {
      toast.error(t("Anfrage konnte nicht gesendet werden. Bitte versuchen Sie es erneut.", "Request could not be sent. Please try again."));
    } finally {
      setSubmitting(false);
    }
  };

  const themen = [
    ["nettopolice", t("Nettopolice / Vorsorge", "Net policy / Provision")],
    ["vertragspruefung", t("Bestehende Vertr\u00e4ge pr\u00fcfen", "Review existing contracts")],
    ["immobilien", t("Real Estate / Immobilien", "Real estate")],
    ["festgeld", t("Festgeldverf\u00fcgbarkeit", "Fixed-term availability")],
    ["allgemein", t("Allgemeine Finanzberatung", "General financial advice")],
  ];

  return (
    <div className="bg-[#F6F7F8]">
      <Seo
        title={t("Berater finden \u2013 Beratung in Ihrer N\u00e4he", "Find an Advisor \u2013 Advice Near You")}
        description={t(
          "PLZ eingeben, Anliegen w\u00e4hlen, pers\u00f6nliche R\u00fcckmeldung erhalten: Finden Sie Ihren Eleven Family Office Ansprechpartner f\u00fcr Vorsorge, Immobilien und Festgeldanfragen.",
          "Enter your postal code, choose your topic, get a personal response: find your Eleven Family Office contact for provision, real estate and fixed-term inquiries."
        )}
      />
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
        <SectionHeading
          eyebrow={t("Berater finden", "Find an advisor")}
          title={t("Finden Sie den passenden Ansprechpartner.", "Find the right contact person.")}
          subtitle={t(
            "Teilen Sie uns Ihre Postleitzahl und Ihr Anliegen mit \u2013 wir ordnen Ihre Anfrage dem passenden Ansprechpartner in Ihrer Region zu und melden uns pers\u00f6nlich bei Ihnen.",
            "Share your postal code and your request \u2013 we assign your inquiry to the right contact in your region and get back to you personally."
          )}
        />

        <div className="mt-10 grid grid-cols-1 lg:grid-cols-5 gap-8">
          <div className="lg:col-span-3">
            <Card className="rounded-[8px] border-border" data-testid="berater-finden-form">
              <CardContent className="p-6 sm:p-8">
                {success ? (
                  <div className="py-10 text-center" data-testid="berater-finden-success">
                    <div className="mx-auto h-14 w-14 rounded-full bg-[#FDEFEC] border border-[#FF3C20]/40 flex items-center justify-center">
                      <CircleCheck className="h-7 w-7 text-[#E8241B]" />
                    </div>
                    <h2 className="mt-5 font-display text-2xl font-semibold text-[#111318]">{t("Vielen Dank f\u00fcr Ihre Anfrage!", "Thank you for your request!")}</h2>
                    <p className="mt-3 text-sm text-[#3B4452] leading-relaxed max-w-md mx-auto">
                      {t(
                        "Wir ordnen Ihre Anfrage jetzt dem passenden Ansprechpartner zu und melden uns in der Regel innerhalb eines Werktages pers\u00f6nlich bei Ihnen.",
                        "We are now assigning your inquiry to the right contact and will usually get back to you personally within one business day."
                      )}
                    </p>
                    <Button asChild className="mt-6 rounded-[6px] bg-[#FF3C20] text-white hover:bg-[#E8241B] font-semibold" data-testid="berater-success-termin">
                      <Link to="/termin">{t("Direkt Termin buchen", "Book appointment directly")}</Link>
                    </Button>
                  </div>
                ) : (
                  <>
                    <h3 className="font-display text-xl font-semibold text-[#111318]">{t("Beratung in Ihrer N\u00e4he anfragen", "Request advice in your area")}</h3>
                    <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-4">
                      <div className="space-y-1.5">
                        <Label className="text-xs text-[#3B4452]">{t("Postleitzahl", "Postal code")} *</Label>
                        <Input
                          value={form.plz}
                          onChange={(e) => set("plz", e.target.value.replace(/\D/g, "").slice(0, 5))}
                          placeholder="z. B. 10115"
                          inputMode="numeric"
                          className={`h-11 rounded-[6px] ${errors.plz ? "border-destructive" : ""}`}
                          data-testid="berater-plz-input"
                        />
                        {errors.plz && <p className="text-xs text-destructive">{t("Bitte g\u00fcltige PLZ angeben", "Please enter a valid postal code")}</p>}
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-[#3B4452]">{t("Beratungsinteresse", "Advisory interest")} *</Label>
                        <Select value={form.thema} onValueChange={(v) => set("thema", v)}>
                          <SelectTrigger className={`h-11 rounded-[6px] ${errors.thema ? "border-destructive" : ""}`} data-testid="berater-thema-select">
                            <SelectValue placeholder={t("Bitte w\u00e4hlen", "Please select")} />
                          </SelectTrigger>
                          <SelectContent>
                            {themen.map(([val, label]) => (
                              <SelectItem key={val} value={val} data-testid={`berater-thema-${val}`}>{label}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-[#3B4452]">{t("Vorname", "First name")} *</Label>
                        <Input value={form.first_name} onChange={(e) => set("first_name", e.target.value)} className={`h-11 rounded-[6px] ${errors.first_name ? "border-destructive" : ""}`} data-testid="berater-firstname-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-[#3B4452]">{t("Nachname", "Last name")} *</Label>
                        <Input value={form.last_name} onChange={(e) => set("last_name", e.target.value)} className={`h-11 rounded-[6px] ${errors.last_name ? "border-destructive" : ""}`} data-testid="berater-lastname-input" />
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-[#3B4452]">E-Mail *</Label>
                        <Input type="email" value={form.email} onChange={(e) => set("email", e.target.value)} className={`h-11 rounded-[6px] ${errors.email ? "border-destructive" : ""}`} data-testid="berater-email-input" />
                        {errors.email && <p className="text-xs text-destructive">{t("Bitte g\u00fcltige E-Mail angeben", "Please enter a valid email")}</p>}
                      </div>
                      <div className="space-y-1.5">
                        <Label className="text-xs text-[#3B4452]">{t("Telefon", "Phone")}</Label>
                        <Input value={form.phone} onChange={(e) => set("phone", e.target.value)} className="h-11 rounded-[6px]" data-testid="berater-phone-input" />
                      </div>
                      <div className="space-y-1.5 sm:col-span-2">
                        <Label className="text-xs text-[#3B4452]">{t("Bevorzugte Kontaktzeit", "Preferred contact time")}</Label>
                        <Select value={form.callback_window} onValueChange={(v) => set("callback_window", v)}>
                          <SelectTrigger className="h-11 rounded-[6px]" data-testid="berater-callback-select">
                            <SelectValue placeholder={t("Egal", "Any time")} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="vormittags">{t("Vormittags (8\u201312 Uhr)", "Morning (8am\u201312pm)")}</SelectItem>
                            <SelectItem value="mittags">{t("Mittags (12\u201315 Uhr)", "Midday (12\u20133pm)")}</SelectItem>
                            <SelectItem value="nachmittags">{t("Nachmittags (15\u201318 Uhr)", "Afternoon (3\u20136pm)")}</SelectItem>
                            <SelectItem value="abends">{t("Abends (18\u201320 Uhr)", "Evening (6\u20138pm)")}</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <label className={`mt-5 flex items-start gap-3 rounded-[6px] border p-4 cursor-pointer ${errors.privacy ? "border-destructive" : "border-border"}`}>
                      <Checkbox checked={privacy} onCheckedChange={(v) => setPrivacy(!!v)} className="mt-0.5" data-testid="berater-consent-checkbox" />
                      <span className="text-xs leading-relaxed text-[#3B4452]">
                        {t("Ich habe die", "I have read the")}{" "}
                        <Link to="/datenschutz" target="_blank" className="underline underline-offset-2 text-[#111318]">{t("Datenschutzerkl\u00e4rung", "privacy policy")}</Link>{" "}
                        {t("gelesen und willige in die Verarbeitung meiner Daten zur Bearbeitung meiner Anfrage ein. *", "and consent to the processing of my data to handle my request. *")}
                      </span>
                    </label>
                    {errors.privacy && <p className="mt-2 text-xs text-destructive">{t("Diese Einwilligung ist erforderlich.", "This consent is required.")}</p>}
                    <Button className="mt-6 w-full h-12 rounded-[6px] bg-[#FF3C20] text-white hover:bg-[#E8241B] font-semibold" onClick={submit} disabled={submitting} data-testid="berater-submit-button">
                      <MapPin className="h-4 w-4 mr-1.5" />
                      {submitting ? t("Wird gesendet\u2026", "Sending\u2026") : t("Beratung in Ihrer N\u00e4he anfragen", "Request advice in your area")}
                    </Button>
                  </>
                )}
              </CardContent>
            </Card>
          </div>

          <aside className="lg:col-span-2">
            <div className="lg:sticky lg:top-28 space-y-5">
              <div className="rounded-[8px] bg-white border border-border p-7">
                <h3 className="font-display text-lg font-semibold text-[#111318]">{t("So funktioniert es", "How it works")}</h3>
                <ol className="mt-5 space-y-5">
                  {[
                    { icon: MapPin, text: t("Sie senden uns Ihre PLZ und Ihr Anliegen.", "You send us your postal code and your request.") },
                    { icon: UserRound, text: t("Wir ordnen Ihre Anfrage dem passenden Ansprechpartner in Ihrer Region zu.", "We assign your inquiry to the right contact in your region.") },
                    { icon: Clock4, text: t("Sie erhalten in der Regel innerhalb eines Werktages eine pers\u00f6nliche R\u00fcckmeldung.", "You usually receive a personal response within one business day.") },
                  ].map((s, i) => (
                    <li key={i} className="flex gap-3.5">
                      <div className="h-9 w-9 rounded-[6px] bg-[#F1F2F4] border border-border flex items-center justify-center shrink-0">
                        <s.icon className="h-4 w-4 text-[#111318]" />
                      </div>
                      <p className="text-sm text-[#3B4452] leading-relaxed pt-1.5">{s.text}</p>
                    </li>
                  ))}
                </ol>
              </div>
              <div className="rounded-[8px] bg-white border border-border p-6 flex items-start gap-3">
                <ShieldCheck className="h-4 w-4 text-[#E8241B] shrink-0 mt-0.5" />
                <p className="text-xs text-[#3B4452] leading-relaxed">
                  {t(
                    "Ihre Daten werden DSGVO-konform verarbeitet. Einwilligungen werden dokumentiert und sind jederzeit widerrufbar.",
                    "Your data is processed in compliance with GDPR. Consents are documented and can be revoked at any time."
                  )}
                </p>
              </div>
              <ComplianceNote>
                {t(
                  "Die pers\u00f6nliche Beratersuche wird derzeit ausgebaut. Ihre Anfrage wird zentral erfasst und dem passenden Ansprechpartner zugeordnet.",
                  "The personal advisor search is currently being expanded. Your inquiry is recorded centrally and assigned to the right contact."
                )}
              </ComplianceNote>
            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
